import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { stockAPI } from "../../lib/api";
import { Package, AlertTriangle, BookOpen, TrendingDown, ArrowRight, RefreshCcw, Plus } from "lucide-react";
import { toast } from "sonner";
import { Header } from '../../components/layout/Header';

const fmtBRL = v => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

function StatCard({ label, value, sub, icon: Icon, color = "text-amber-400", bg = "bg-amber-900/20" }) {
    return (
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
                <div className={`p-2 rounded-lg ${bg}`}><Icon className={`w-4 h-4 ${color}`} /></div>
                <span className="text-xs text-stone-400">{label}</span>
            </div>
            <p className="text-2xl font-bold text-stone-100">{value ?? "—"}</p>
            {sub && <p className="text-xs text-stone-500 mt-0.5">{sub}</p>}
        </div>
    );
}

export default function StockDashboard() {
    const [summary, setSummary] = useState(null);
    const [products, setProducts] = useState([]);
    const [movements, setMovements] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [s, p, m, a] = await Promise.all([
                stockAPI.summary(),
                stockAPI.products(),
                stockAPI.movements({ limit: 10 }),
                stockAPI.alerts(),
            ]);
            setSummary(s.data);
            setProducts(p.data || []);
            setMovements(m.data?.movements || []);
            setAlerts(a.data || []);
        } catch { toast.error("Erro ao carregar estoque"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    // Valor total em estoque
    const totalValue = products.reduce((acc, p) => acc + (p.stock_current || 0) * (p.price || 0), 0);

    return (
        <>
        <Header title="Estoque — Livros" subtitle="Controle de tiragens e movimentações" actions={<><button onClick={load} className="p-2 border border-stone-700 rounded-lg text-stone-400 hover:text-stone-200 transition-colors"><RefreshCcw className="w-4 h-4" /></button><Link to="/estoque/entrada" className="flex items-center gap-2 bg-amber-700 hover:bg-amber-600 text-white text-sm px-4 py-2 rounded-lg transition-colors"><Plus className="w-4 h-4" /> Entrada de Estoque</Link></>} />
        <div className="p-6 space-y-6">

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Total de produtos" value={summary?.total_products} icon={Package} color="text-amber-400" bg="bg-amber-900/20" />
                <StatCard label="Valor em estoque" value={fmtBRL(totalValue)} icon={Package} color="text-emerald-400" bg="bg-emerald-900/20" />
                <StatCard label="Estoque baixo" value={summary?.low_stock}
                    sub={summary?.low_stock > 0 ? "Reposição necessária" : "OK"}
                    icon={TrendingDown}
                    color={summary?.low_stock > 0 ? "text-red-400" : "text-emerald-400"}
                    bg={summary?.low_stock > 0 ? "bg-red-900/20" : "bg-emerald-900/20"} />
                <StatCard label="Alertas ativos" value={summary?.active_alerts} icon={AlertTriangle}
                    color={summary?.active_alerts > 0 ? "text-orange-400" : "text-stone-500"}
                    bg={summary?.active_alerts > 0 ? "bg-orange-900/20" : "bg-stone-800"} />
            </div>

            {/* Alertas */}
            {alerts.length > 0 && (
                <div className="space-y-2">
                    <h2 className="text-sm font-semibold text-stone-300 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-orange-400" /> Alertas de Estoque
                    </h2>
                    {alerts.map(a => (
                        <div key={a.id} className="bg-orange-950/30 border border-orange-800/50 rounded-xl p-4 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-orange-200">{a.product_name}</p>
                                <p className="text-xs text-orange-400/70 mt-0.5">
                                    Estoque atual: <strong>{a.stock_current}</strong> · Mínimo: {a.stock_min}
                                </p>
                            </div>
                            <Link to={`/estoque/entrada`}
                                className="text-xs text-orange-300 hover:text-orange-100 border border-orange-700 rounded-lg px-3 py-1.5 transition-colors">
                                Repor →
                            </Link>
                        </div>
                    ))}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Produtos */}
                <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-sm font-semibold text-stone-200">Produtos</h2>
                        <Link to="/estoque/produtos" className="text-xs text-amber-500 hover:text-amber-400 flex items-center gap-1">
                            Ver todos <ArrowRight className="w-3 h-3" />
                        </Link>
                    </div>
                    {loading ? (
                        <p className="text-stone-500 text-xs text-center py-6">Carregando...</p>
                    ) : products.length === 0 ? (
                        <div className="text-center py-6">
                            <Package className="w-8 h-8 text-stone-700 mx-auto mb-2" />
                            <p className="text-xs text-stone-500">Nenhum produto cadastrado.</p>
                            <Link to="/estoque/produtos" className="text-xs text-amber-500 hover:text-amber-400 mt-1 inline-block">Cadastrar produto →</Link>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {products.slice(0, 6).map(p => {
                                const low = p.stock_current <= p.stock_min;
                                return (
                                    <Link key={p.id} to={`/estoque/produtos/${p.id}`}
                                        className="flex items-center gap-3 hover:bg-stone-800/50 rounded-lg p-2 -mx-2 transition-colors group">
                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${low ? "bg-red-900/30" : "bg-amber-900/20"}`}>
                                            <BookOpen className={`w-4 h-4 ${low ? "text-red-400" : "text-amber-400"}`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-stone-200 truncate">{p.name}</p>
                                            {p.isbn && <p className="text-xs text-stone-500">ISBN {p.isbn}</p>}
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <p className={`text-sm font-bold ${low ? "text-red-400" : "text-stone-100"}`}>{p.stock_current}</p>
                                            <p className="text-xs text-stone-600">un.</p>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Últimas movimentações */}
                <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-sm font-semibold text-stone-200">Últimas movimentações</h2>
                        <Link to="/estoque/movimentacoes" className="text-xs text-amber-500 hover:text-amber-400 flex items-center gap-1">
                            Ver todas <ArrowRight className="w-3 h-3" />
                        </Link>
                    </div>
                    {movements.length === 0 ? (
                        <p className="text-xs text-stone-500 text-center py-6">Nenhuma movimentação.</p>
                    ) : (
                        <div className="space-y-2">
                            {movements.map(m => (
                                <div key={m.id} className="flex items-center gap-3 py-2 border-b border-stone-800/50 last:border-0">
                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                        m.type === "entrada" ? "bg-green-500" :
                                        m.type === "saida"   ? "bg-amber-500" : "bg-blue-500"
                                    }`} />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-stone-300 truncate">{m.product_id}</p>
                                        <p className="text-xs text-stone-500">
                                            {m.type} · {m.reason || "—"}
                                            {m.buyer_name && ` · ${m.buyer_name}`}
                                        </p>
                                    </div>
                                    <span className={`text-sm font-bold flex-shrink-0 ${
                                        m.type === "entrada" ? "text-green-400" :
                                        m.type === "saida"   ? "text-red-400"  : "text-blue-400"
                                    }`}>
                                        {m.type === "entrada" ? "+" : m.type === "saida" ? "-" : "~"}{m.quantity}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
        </>
    );
}
